import { AddressFields, Checkbox, SubmitButton, TextInput } from '@components/common';
import { townDropdownData, YesNoVal } from '@config/constant';
import { useFormik } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { PrimaryDetailList } from './primaryDetailList';
import {
	defaultAccordianDetails,
	defaultCompetitionDetails,
	defaultMoreDetailFormInitialValues,
	defaultPrimaryDetails,
	FactoryFields,
	IMoredetailIntialValue,
	toNumberOrNull,
	WorkshopFields,
} from '.';
import { CompetitionDetailList } from './competitionDetails';
import { AccordianDetailList } from './accordianDetailList';
import { CarpenterDetails } from './carpenterDetails';
import { useMoreDetailsMutation } from '@services/hooks/enduser-details/useMoreDetailsMutation';
import { useNavigate } from 'react-router-dom';
import { useValidation } from '@hooks/validations';
import {
	compareFactoryAddresses,
	compareWorkShopAddresses,
	getArchitectDetails,
	getCompetitionArray,
	getDealerDetails,
	keyDownEvent,
	RoleType,
} from '@helper/utils';
import { useMoreDetailsQuery } from '@services/hooks/enduser-details/useMoreDetailQuery';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useEndUserStore } from '@store/enduser';
import { useLeadUserQuery } from '@services/hooks/endusers/leadUserQuery';
import { useAllEndUserQuery } from '@services/hooks/endusers/allEndUserQuery';
import { useLmsUserLeadsQuery } from '@services/hooks/lms/userLeads';
import { ToastType, useTosterStore } from '@store/toster';
import { SixMonthsConfirmationModal } from '@components/contractor360/components';
import { Modal } from '@components/common/modal/modal';
import {
	IMoreDetailsRevalidateField,
	initialMoreDetailsValidateField,
	useEndUserProfileStore,
} from '@store/endUserProfileStore';
import { checkAllFieldsValid } from '@helper/formControl';
import { UpdateSubcontractorsDetailMutation } from '@services/hooks/enduser-details/useContractorDetailsMutation';
import { SubContractorList } from '@components/endUserDetails/components/subContractorList';
import { useComponentLevelAccessCheck } from '../../../../authGuard/hooks/useComponentLevelAccessCheck';
import { useStoreFormikFormValue } from '@store/setFormikFormValueStore';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { useGetRxDb } from '@hooks/getRxdbData';
import { BDEIMRDetail, ClubDetails, MoreDetails } from '@services/hooks/enduser-details';
import { ProfileData } from '@services/hooks/profile';
import { resolveClubTitleFromBusinessDetail, useClubDropDownQuery } from '@services/hooks/endusers/clubListQuery';
import type { SecondaryClub, TClubDetails } from '../kycDetailsForm';
import { useQueryClient } from '@tanstack/react-query';
import { useSuccessTosterStore } from '@store/successToster';
import { useKaeSecondaryClubQuery } from '@services/hooks/enduser-details/useKaeSecondaryClubQuery';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import Dropdown from '@components/common/formControl/dropdown/dropdown';
import { ConfirmationModal } from '@components/conductedMeetings/confirmationModal/confirmationModal';
import { useGetBdeImrDetails } from '@services/hooks/enduser-details/useGetBdeImrDetails';
import { useIsImpersonationLoginUser } from '../../../../authGuard/hooks/useIsImpersonationLoginUser';

export const MoreDetailsForm = (): ReactElement => {
	const queryClient = useQueryClient();
	const nav = useNavigate();
	const {
		isEditing,
		setIsEditing,
		isFormEdit,
		formId,
		selectedSubContractor: selectedStoreValue,
		setActiveNavTab,
	} = useEndUserStore();
	const { setSuccessModalOpen } = useSuccessTosterStore();
	const { result: profile } = useGetRxDb<ProfileData>('profile');

	const { data: getMoreDetailFormData, isFetching: isMoreDetailFetching } = useMoreDetailsQuery(formId, isFormEdit);
	const {
		mutate,
		isPending: isMoreDetailFormPending,
		error: isMoreDetailFormError,
	} = useMoreDetailsMutation(formId, isFormEdit);
	const { MoreDetailsValidationSchema } = useValidation();
	const { refetch: leadUserRefetch } = useLeadUserQuery();
	const { refetch: allEndUserRefetch } = useAllEndUserQuery();
	const { refetch: lmsUserLeadsRefetch } = useLmsUserLeadsQuery();
	const { refetch: bdeOfImrDetailFetch } = useGetBdeImrDetails();
	const { setToaster } = useTosterStore();
	const [isOpen, setModal] = useState<boolean>(false);
	const [isMoreDetailsUpdateOpen, setMoreDetailsUpdateOpen] = useState<boolean>(false);
	const [userType, setUserType] = useState<string>(profile?.[0]?.usertype_c ?? '');

	const {
		moreDetailsRevalidate,
		setKeyBaseValue: setEndUserKeyBaseValue,
		moreDetailFormRevalidateField,
	} = useEndUserProfileStore();
	const [areAllFieldsValid, setAreAllFieldsValid] = useState<boolean>(true);
	const { mutate: UpdateContractorDetails } = UpdateSubcontractorsDetailMutation(getMoreDetailFormData?.membershipno_c);
	const checkSubContractorPermission = useComponentLevelAccessCheck('sub-contractor');
	const { setFormikValuesToStore, storedFormikValues } = useStoreFormikFormValue();
	const { result: clubDetails } = useGetRxDb<ClubDetails>('club');
	const { data: clubDropdownFetchData, isFetching: isClubDropdownFetching } = useClubDropDownQuery(false, formId);
	const checkSecondaryClubPermissions = useComponentLevelAccessCheck('seconday-club-dropdown');
	const [secondaryClubList, setSecondaryClubList] = useState<DropdownOptions[]>([]);
	const [selectedClubId, setSelectedClubId] = useState<string | null>(null);
	const { data: secondaryClubData } = useKaeSecondaryClubQuery(selectedClubId);
	const isImpersonationLoginUser = useIsImpersonationLoginUser();

	const clubsSource = useMemo((): ClubDetails[] => {
		if (clubDropdownFetchData?.length) {
			return clubDropdownFetchData;
		}
		return clubDetails ?? [];
	}, [clubDropdownFetchData, clubDetails]);

	const clubList = useMemo((): TClubDetails[] => {
		if (!clubsSource.length) {
			return [];
		}
		return clubsSource.map((item) => ({
			id: item.id,
			value: item.name,
		}));
	}, [clubsSource]);

	useEffect(() => {
		if (moreDetailsRevalidate) {
			const isValid = checkAllFieldsValid(moreDetailFormRevalidateField);
			setAreAllFieldsValid(isValid);
		}
	}, [moreDetailsRevalidate, moreDetailFormRevalidateField]);

	const formik = useFormik({
		initialValues: defaultMoreDetailFormInitialValues,
		onSubmit: () => {
			void handleSubmitForm();
		},
		validateOnMount: false,
		validationSchema: MoreDetailsValidationSchema,
	});

	const userRoleType = useUserRoleType();
	const checkNextButtonAccessforSS = useComponentLevelAccessCheck('ss-disable-button');
	const isKAEcarpenterField = useComponentLevelAccessCheck('kae-carpenter-field');
	const isRBDMNextBtn = useComponentLevelAccessCheck('rbdm-next-btn');
	const isBDESEUser = useComponentLevelAccessCheck('BDE-SE-USER');
	const checkBDEPermission = useComponentLevelAccessCheck('bde-code-field');
	const bdeOfImr = useMemo(async (): Promise<BDEIMRDetail | null | undefined> => {
		if (!checkBDEPermission || !bdeOfImrDetailFetch) return null;

		try {
			const response = await bdeOfImrDetailFetch();
			return response?.data?.data?.find((b) => b.mdi_code_c === formik.values.bde_code);
		} catch (error) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-expect-error
			setToaster(true, ToastType.error, error?.message);
		}
	}, [checkBDEPermission, bdeOfImrDetailFetch, setToaster, formik.values.bde_code]);
	const isBDERole = userRoleType === 'BDE';
	const isKAEUser = userType === RoleType.KAE;
	const isIMRUser = userType === RoleType.IMR;
	const checkKaePermissions = useComponentLevelAccessCheck('has-permission');
	const checkCarpenterPermissions = useComponentLevelAccessCheck('has-carpenter-permission');
	const showCarpenterFields = isFormEdit && (!checkKaePermissions || checkCarpenterPermissions);

	useEffect(() => {
		if (userRoleType) {
			formik.setFieldValue('role', userRoleType);
			setUserType(userRoleType);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userRoleType]);

	useEffect(() => {
		if (!isFormEdit || !moreDetailsRevalidate) return;

		const updatedValue = { ...moreDetailFormRevalidateField };
		const shouldValidateWorkshopAddress = isKAEUser
			? formik.values.workshop_checkbox === 'Yes' || formik.values.what_kind_of_jobs_do_you_undertake === 'Yes'
			: Boolean(formik.values.workshop_checkbox);
		const shouldValidateFactoryAddress = isKAEUser
			? formik.values.factory_checkbox === 'Yes' || formik.values.work_do_you_generally_undertake === 'Yes'
			: Boolean(formik.values.factory_checkbox);

		if (!shouldValidateWorkshopAddress) {
			WorkshopFields.forEach((field) => {
				updatedValue[field] = true;
			});
		}

		if (!shouldValidateFactoryAddress) {
			FactoryFields.forEach((field) => {
				updatedValue[field] = true;
			});
		}
		setEndUserKeyBaseValue('moreDetailFormRevalidateField', updatedValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formik?.values?.primary_details,
		formik?.values?.workshop_checkbox,
		formik?.values?.what_kind_of_jobs_do_you_undertake,
		formik?.values?.factory_checkbox,
		formik?.values?.work_do_you_generally_undertake,
		moreDetailsRevalidate,
		isFormEdit,
		isKAEUser,
	]);

	useEffect(() => {
		if (!formId) {
			nav('/add-member-details/basic-details');
		}
	}, [formId, nav, getMoreDetailFormData]);

	const submitSubContractorDetails = (): void => {
		if (
			selectedStoreValue?.length &&
			getMoreDetailFormData &&
			(getMoreDetailFormData?.monthlyconsumption_c as number) > 300 &&
			!checkSubContractorPermission
		) {
			const outputArray = selectedStoreValue.map((item: string) => ({
				membership_no: item,
				end_user_membershipno: getMoreDetailFormData?.membershipno_c,
			}));
			UpdateContractorDetails(outputArray, {
				onError: (error) => {
					setToaster(true, ToastType.error, error?.message);
				},
			});
		}
	};

	const getSelectedClubSfid = (clubDetails?.find((c) => c.name === formik.values.club)?.sfid as string) ?? null;
	const secondaryClubId =
		(secondaryClubList?.find((c) => c.value === formik.values.secondary_club)?.id as string) ?? null;
	const getSecondaryClubValue = (): string | null => {
		if (isBDERole) {
			return getSelectedClubSfid;
		}
		return secondaryClubId;
	};

	const handleSubmitForm = useCallback(async () => {
		const clubId = clubList?.find((c) => c.value === formik.values.club)?.id ?? null;
		const hasWorkshopAddress = isKAEUser
			? formik.values.workshop_checkbox === 'Yes' || formik.values.what_kind_of_jobs_do_you_undertake === 'Yes'
			: Boolean(formik.values.workshop_checkbox);
		const hasFactoryAddress = isKAEUser
			? formik.values.factory_checkbox === 'Yes' || formik.values.work_do_you_generally_undertake === 'Yes'
			: Boolean(formik.values.factory_checkbox);
		const hasArchitectDetails = isKAEUser ? formik.values.interior_work === 'No' : true;
		const isCarpenter = formik.values.enable_carpenter_option === 'Yes';

		let payload: MoreDetails = {
			primarydealercode: formik.values.primary_details[0]?.dealer_code?.split('-')[0]?.trim() ?? null,
			primarydealername: formik.values.primary_details[0]?.dealer_name ?? null,
			alternate1dealername_c: formik.values.primary_details[1]?.dealer_name ?? null,
			alternate1dealercode_c: formik.values.primary_details[1]?.dealer_code?.split('-')[0]?.trim() ?? null,
			alternate2dealername_c: formik.values.primary_details[2]?.dealer_name ?? null,
			alternate2dealercode_c: formik.values.primary_details[2]?.dealer_code?.split('-')[0]?.trim() ?? null,
			monthlyconsumption_c: formik.values.average_glue_potential,
			no_of_carpenters: formik.values.no_of_carpenters,
			workshoppincode_c: hasWorkshopAddress ? formik.values.workshop_zipcode : null,
			workshopstate_c: hasWorkshopAddress ? formik.values.workshop_state : null,
			workshop_c: hasWorkshopAddress,
			workshopcity_c: hasWorkshopAddress ? formik.values.workshop_city : null,
			workshopaddress1_c: hasWorkshopAddress ? formik.values.workshop_address : null,
			workshopaddress2_c: hasWorkshopAddress ? formik.values.workshop_street : null,
			status: 'More Details',
			...(!isIMRUser ? { convert_lead_c: 'True' } : {}),
			club_mapping: clubId,
			secondary_club_c: getSecondaryClubValue(),
			...(showCarpenterFields
				? {
						is_competition_filled_c: isCarpenter,
						owner_name_c: isCarpenter ? formik.values.boss_name : null,
						owner_mobile_number_c: isCarpenter ? formik.values.boss_mobile : null,
					}
				: {}),
			competition_brand_1_consumption_c: toNumberOrNull(formik.values.competition_details[0]?.competition_volume),
			competition_brand_1_used_c:
				formik.values.competition_details[0]?.other_competition_brands ??
				formik.values.competition_details[0]?.competition_brands ??
				null,
			competition_brand_2_consumption_c: toNumberOrNull(formik.values.competition_details[1]?.competition_volume),
			competition_brand_2_used_c:
				formik.values.competition_details[1]?.other_competition_brands ??
				formik.values.competition_details[1]?.competition_brands ??
				null,
			competition_brand_3_consumption_c: toNumberOrNull(formik.values.competition_details[2]?.competition_volume),
			competition_brand_3_used_c:
				formik.values.competition_details[2]?.other_competition_brands ??
				formik.values.competition_details[2]?.competition_brands ??
				null,
			furniture_address_c: hasFactoryAddress ? formik.values.factory_address : null,
			furniture_city_c: hasFactoryAddress ? formik.values.factory_city : null,
			furniture_pincode_c: hasFactoryAddress ? formik.values.factory_zipcode : null,
			furniture_state_c: hasFactoryAddress ? formik.values.factory_state : null,
			additionenduser: {
				architect_name1_c: hasArchitectDetails ? formik.values.architect_details[0]?.name : null,
				architect_phone1_c: hasArchitectDetails ? toNumberOrNull(formik.values.architect_details[0]?.number) : null,
				architect_name2_c: hasArchitectDetails ? formik.values.architect_details[1]?.name : null,
				architect_phone2_c: hasArchitectDetails ? toNumberOrNull(formik.values.architect_details[1]?.number) : null,
				architect_name3_c: hasArchitectDetails ? formik.values.architect_details[2]?.name : null,
				architect_phone3_c: hasArchitectDetails ? toNumberOrNull(formik.values.architect_details[2]?.number) : null,
				what_kind_of_jobs_do_you_undertake_c:
					isKAEUser && formik.values.workshop_checkbox === 'No'
						? formik.values.what_kind_of_jobs_do_you_undertake
						: null,
				work_do_you_generally_undertake_c:
					isKAEUser && formik.values.factory_checkbox === 'No' ? formik.values.work_do_you_generally_undertake : null,
			},
		};
		if (checkBDEPermission) {
			const bdeData = await bdeOfImr;
			payload = {
				...payload,
				imrcode_c: profile[0]?.mdicode_c ?? null,
				mdicode: bdeData?.mdi_code_c ?? null,
				assigned_cmdi: bdeData?.assigned_mdi_c ?? null,
			};
		}
		if (formId) {
			mutate(payload, {
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey: ['kycDetails'] });
					queryClient.invalidateQueries({ queryKey: ['moreDetails'] });
					setEndUserKeyBaseValue('moreDetailsRevalidate', false);
					if (!isFormEdit) submitSubContractorDetails();

					if (isBDESEUser) {
						allEndUserRefetch?.();
						leadUserRefetch?.();
					} else {
						lmsUserLeadsRefetch?.();
					}
					setActiveNavTab([0, 1, 2]);
					if (checkBDEPermission) {
						setToaster(true, ToastType.success, 'Your member details have been saved successfully!');
						nav('/end-users/new-leads', { replace: true });
						return;
					}
					const redirectPath = isBDESEUser ? '/end-users/all' : '/lms/users';
					setSuccessModalOpen(redirectPath, 'Your member details have been saved successfully!');
				},
				onError: (error) => {
					setToaster(true, ToastType.error, error?.message);
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formik.values,
		formId,
		mutate,
		nav,
		isFormEdit,
		getMoreDetailFormData,
		clubList,
		secondaryClubList,
		queryClient,
		setActiveNavTab,
		checkBDEPermission,
		isBDESEUser,
		setSuccessModalOpen,
		setToaster,
		allEndUserRefetch,
		leadUserRefetch,
		bdeOfImr,
		profile,
		isKAEUser,
		showCarpenterFields,
	]);

	const handleMoreDetailsConfirmation = (fn: () => void): void => {
		if (moreDetailsRevalidate) {
			setModal(true);
			return;
		}
		fn();
	};

	const handleSubmitClick = (): void => {
		handleMoreDetailsConfirmation(() => {
			formik.handleSubmit();
		});
	};

	const setFormikValueValidate = (values: IMoredetailIntialValue): void => {
		formik.validateForm(values).then((errors) => {
			Object.keys(errors).forEach((fieldName) => {
				formik.setFieldTouched(fieldName, true, true);
			});
		});
	};

	const getKAEWorkshopValue = (): string => {
		const hasWorkshopAddress = !compareWorkShopAddresses(getMoreDetailFormData);

		if (getMoreDetailFormData?.additionenduser?.what_kind_of_jobs_do_you_undertake_c) {
			return 'No';
		}

		if (hasWorkshopAddress) {
			return 'Yes';
		}

		return '';
	};

	const getKAEFactoryValue = (): string => {
		const hasFactoryAddress = !compareFactoryAddresses(getMoreDetailFormData);

		if (getMoreDetailFormData?.additionenduser?.work_do_you_generally_undertake_c) {
			return 'No';
		}

		if (hasFactoryAddress) {
			return 'Yes';
		}

		return '';
	};

	const getInteriorValue = (): string => {
		const architectDetailsArr = getArchitectDetails(getMoreDetailFormData);
		const primaryDetailsArr = getDealerDetails(getMoreDetailFormData);

		if (architectDetailsArr.length > 0) {
			return 'No';
		}
		if (primaryDetailsArr.length > 0) {
			return 'Yes';
		}

		return '';
	};

	const getCarpenterValue = (): IMoredetailIntialValue['enable_carpenter_option'] =>
		getMoreDetailFormData?.is_competition_filled_c ? 'Yes' : 'No';

	useEffect(() => {
		setMoreDetailsUpdateOpen(false);
	}, [formId, isFormEdit]);

	useEffect(() => {
		if (storedFormikValues) {
			formik.setValues(storedFormikValues);
			setFormikValueValidate(storedFormikValues);
			return;
		}
		if (formId && getMoreDetailFormData && Object.keys(getMoreDetailFormData).length > 0) {
			const primaryDetailsArr = getDealerDetails(getMoreDetailFormData);
			const architectDetailsArr = getArchitectDetails(getMoreDetailFormData);
			const competitionDetailsArr = getCompetitionArray(getMoreDetailFormData);
			const needsClubListToResolve =
				!String(getMoreDetailFormData.clubname_c ?? '').trim() &&
				String(getMoreDetailFormData.club_mapping ?? '').trim() !== '';
			if (needsClubListToResolve && clubsSource.length === 0) {
				return;
			}
			const resolvedClubName = resolveClubTitleFromBusinessDetail(getMoreDetailFormData, clubsSource);
			const selectedClub = clubsSource.find((c) => c.name === resolvedClubName);
			const isKAEUserRole = userType === RoleType.KAE;

			const values = {
				...formik.values,
				primary_details: primaryDetailsArr.length > 0 ? primaryDetailsArr : defaultPrimaryDetails,
				club: resolvedClubName,
				average_glue_potential: getMoreDetailFormData.monthlyconsumption_c,
				no_of_carpenters: getMoreDetailFormData.no_of_carpenters,
				workshop_zipcode: getMoreDetailFormData.workshoppincode_c,
				workshop_state: getMoreDetailFormData.workshopstate_c,
				workshop_city: getMoreDetailFormData.workshopcity_c,
				workshop_address: getMoreDetailFormData.workshopaddress1_c,
				workshop_street: getMoreDetailFormData.workshopaddress2_c,
				workshop_checkbox: isKAEUserRole ? getKAEWorkshopValue() : !compareWorkShopAddresses(getMoreDetailFormData),
				interior_work: isKAEUserRole ? getInteriorValue() : '',
				competition_details: competitionDetailsArr.length > 0 ? competitionDetailsArr : defaultCompetitionDetails,
				architect_details: architectDetailsArr.length > 0 ? architectDetailsArr : defaultAccordianDetails,
				factory_address: getMoreDetailFormData?.furniture_address_c,
				factory_city: getMoreDetailFormData?.furniture_city_c,
				factory_state: getMoreDetailFormData?.furniture_state_c,
				factory_zipcode: getMoreDetailFormData?.furniture_pincode_c,
				bde_code: isEditing && getMoreDetailFormData.mdicode ? getMoreDetailFormData.mdicode : '',
				factory_checkbox: isKAEUserRole ? getKAEFactoryValue() : !compareFactoryAddresses(getMoreDetailFormData),
				what_kind_of_jobs_do_you_undertake:
					getMoreDetailFormData?.additionenduser?.what_kind_of_jobs_do_you_undertake_c ?? '',
				work_do_you_generally_undertake:
					getMoreDetailFormData?.additionenduser?.work_do_you_generally_undertake_c ?? '',
				enable_carpenter_option: getCarpenterValue(),
				boss_name: getMoreDetailFormData?.owner_name_c ?? '',
				boss_mobile: getMoreDetailFormData?.owner_mobile_number_c ?? '',
				role: userRoleType,
			};
			setSelectedClubId(selectedClub?.sfid ?? null);

			formik.setValues(values);
			if (isFormEdit) {
				setFormikValueValidate(values);
			}
			const primaryDealerCode = values.primary_details?.[0]?.dealer_code;

			const isPrimaryDealerAvailable =
				primaryDealerCode !== null && primaryDealerCode !== undefined && primaryDealerCode !== '';

			const isWorkshopDetailsMissing =
				values.workshop_checkbox !== 'Yes' && values.what_kind_of_jobs_do_you_undertake === '';

			const isFactoryDetailsMissing =
				values.factory_checkbox !== 'Yes' && values.work_do_you_generally_undertake === '';

			if (
				isFormEdit &&
				isPrimaryDealerAvailable &&
				isKAEUser &&
				(isWorkshopDetailsMissing || isFactoryDetailsMissing)
			) {
				setMoreDetailsUpdateOpen(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getMoreDetailFormData, formId, isFormEdit, storedFormikValues, clubsSource, userType]);

	useEffect(() => {
		if (moreDetailsRevalidate && getMoreDetailFormData && isFormEdit) {
			const resetRevalidationFields = (obj: IMoreDetailsRevalidateField): IMoreDetailsRevalidateField => {
				return Object.keys(obj).reduce((acc, key) => {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-expect-error
					acc[key] = Array.isArray(obj[key]) ? new Array(obj[key].length).fill(false) : false;
					return acc;
				}, {} as IMoreDetailsRevalidateField);
			};

			const updatedValue = resetRevalidationFields({
				...moreDetailFormRevalidateField,
				primary_details: new Array(formik.values.primary_details?.length || 1).fill(false),
				competition_brand: new Array(formik.values.competition_details?.length || 1).fill(false),
				competition_volume: new Array(formik.values.competition_details?.length || 1).fill(false),
				architect_name: new Array(formik.values.architect_details?.length || 1).fill(false),
				architect_number: new Array(formik.values.architect_details?.length || 1).fill(false),
			});

			setEndUserKeyBaseValue('moreDetailFormRevalidateField', updatedValue);
		}

		return (): void => {
			setEndUserKeyBaseValue('upperFormRevalidateFiled', initialMoreDetailsValidateField);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getMoreDetailFormData, isFormEdit, moreDetailsRevalidate]);

	const handleCheckboxChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name } = event.target;
			formik.handleChange(event);

			if (moreDetailsRevalidate) {
				const updatedValidationFields = { ...moreDetailFormRevalidateField };
				if (name === 'workshop_checkbox') {
					WorkshopFields.forEach((field) => {
						updatedValidationFields[field] = true;
					});
				}

				if (name === 'factory_checkbox') {
					FactoryFields.forEach((field) => {
						updatedValidationFields[field] = true;
					});
				}
				setEndUserKeyBaseValue('moreDetailFormRevalidateField', updatedValidationFields);
			}
		},
		[formik, moreDetailsRevalidate, moreDetailFormRevalidateField, setEndUserKeyBaseValue]
	);

	const mapSecondaryClubs = (data: SecondaryClub[]): DropdownOptions[] => {
		return data.map((item) => ({
			id: item.sfid,
			value: item.name,
		}));
	};

	useEffect(() => {
		setSecondaryClubList(mapSecondaryClubs(secondaryClubData || []));
	}, [secondaryClubData]);

	useEffect(() => {
		if (getMoreDetailFormData?.secondary_club_c && secondaryClubList.length) {
			const secondaryClubObj = secondaryClubList.find((c) => c.id === getMoreDetailFormData.secondary_club_c);

			formik.setFieldValue('secondary_club', secondaryClubObj?.value || '');
		}
	}, [secondaryClubList, getMoreDetailFormData]);

	const setBEDCode = useCallback(
		(club: DropdownOptions): void => {
			const selectedClub = clubsSource.find((c) => c.id === club.id) ?? clubDetails.find((c) => c.id === club.id);
			if (!checkSecondaryClubPermissions) {
				setSelectedClubId(selectedClub?.sfid ?? null);
				formik.setFieldValue('secondary_club', '');
			}
			if (!checkBDEPermission) return;
			formik.setFieldValue('bde_code', selectedClub?.mdi_code_c ?? '');
		},
		[clubsSource, clubDetails, formik, checkBDEPermission, checkSecondaryClubPermissions]
	);

	return (
		<>
			<div>
				<div className={`all-user-details-form ${moreDetailsRevalidate ? 'end-user-verification-highlight' : ''}`}>
					<Wrapper isLoaderVisible={isMoreDetailFetching || isClubDropdownFetching} isError={isMoreDetailFormError}>
						<form onSubmit={formik.handleSubmit}>
							<ul className="user-details-wrapper row">
								{formik.values.primary_details.map((dealer, index) => (
									<PrimaryDetailList
										key={dealer.id}
										formik={formik}
										primaryListIndex={index}
										dealer={dealer}
										fieldName="primary_details"
										isEditing={isEditing}
									/>
								))}

								<li className={`form-control d-col d-col-2`}>
									<TextInput
										id="average_glue_potential"
										name="average_glue_potential"
										type="text"
										onBlur={formik.handleBlur}
										value={formik.values.average_glue_potential ?? ''}
										setFieldValue={formik.setFieldValue}
										label="Avg monthly Glue Potential (kgs)"
										error={
											formik.touched.average_glue_potential && formik.errors.average_glue_potential
												? formik.errors.average_glue_potential
												: null
										}
										onKeyDown={keyDownEvent}
										disabled
										required
									/>
								</li>

								{isKAEcarpenterField && (
									<li className={`form-control d-col d-col-2`}>
										<TextInput
											id="no_of_carpenters"
											name="no_of_carpenters"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.no_of_carpenters ?? ''}
											setFieldValue={formik.setFieldValue}
											label="No. of carpenters"
											error={
												formik.touched.no_of_carpenters && formik.errors.no_of_carpenters
													? formik.errors.no_of_carpenters
													: null
											}
											onKeyDown={keyDownEvent}
											disabled={isEditing}
											required
										/>
									</li>
								)}

								<li className="form-control d-col d-col-2">
									<Dropdown
										id="club"
										name="club"
										label="Select Club"
										title={formik.values.club ?? ''}
										onBlur={formik.handleBlur}
										error={formik.touched.club && formik.errors.club ? (formik.errors.club as string) : null}
										options={clubList}
										setFieldValue={formik.setFieldValue}
										disabled={isEditing}
										onChange={setBEDCode}
										required
									/>
								</li>

								{showCarpenterFields && (
									<CarpenterDetails
										formik={formik}
										isEditing={isEditing}
										isCarpenterSaved={Boolean(getMoreDetailFormData?.is_competition_filled_c)}
									/>
								)}

								{checkBDEPermission && (
									<li className="form-control d-col d-col-2">
										<TextInput
											id="bde_code"
											name="bde_code"
											type="text"
											value={formik.values.bde_code ?? ''}
											label="BDE Code"
											disabled
										/>
									</li>
								)}

								{!checkSecondaryClubPermissions && (
									<li className="d-col d-col-2">
										<Dropdown
											id="secondary_club"
											name="secondary_club"
											label="Select BDE Club"
											title={formik.values.secondary_club ?? ''}
											onBlur={formik.handleBlur}
											error={
												formik.touched.secondary_club && formik.errors.secondary_club
													? (formik.errors.secondary_club as string)
													: null
											}
											options={secondaryClubList}
											setFieldValue={formik.setFieldValue}
											disabled={isEditing}
										/>
									</li>
								)}

								{formik.values.competition_details.map((competition, index) => (
									<CompetitionDetailList
										key={competition.id}
										formik={formik}
										competitionListIndex={index}
										competition={competition}
										fieldName="competition_details"
										isEditing={isEditing}
									/>
								))}

								{isKAEUser ? (
									<>
										<li className={`form-control d-col d-col-2`}>
											<Dropdown
												id="workshop_checkbox"
												name="workshop_checkbox"
												label="Do you have a workshop?"
												title={formik.values.workshop_checkbox ?? ''}
												onBlur={formik.handleBlur}
												options={YesNoVal}
												setFieldValue={formik.setFieldValue}
												error={
													formik.touched.workshop_checkbox && formik.errors.workshop_checkbox
														? formik.errors.workshop_checkbox
														: null
												}
												disabled={isEditing}
												required
											/>
										</li>

										{formik.values.workshop_checkbox === 'No' && (
											<li className={`form-control d-col d-col-2`}>
												<Dropdown
													id="what_kind_of_jobs_do_you_undertake"
													name="what_kind_of_jobs_do_you_undertake"
													label="Do you get job work?"
													title={formik.values.what_kind_of_jobs_do_you_undertake ?? ''}
													onBlur={formik.handleBlur}
													options={YesNoVal}
													setFieldValue={formik.setFieldValue}
													error={
														formik.touched.what_kind_of_jobs_do_you_undertake &&
														formik.errors.what_kind_of_jobs_do_you_undertake
															? formik.errors.what_kind_of_jobs_do_you_undertake
															: null
													}
													disabled={isEditing}
													required
												/>
											</li>
										)}

										{(formik.values.workshop_checkbox === 'Yes' ||
											formik.values.what_kind_of_jobs_do_you_undertake === 'Yes') && (
											<AddressFields
												mainFieldName="workshop"
												formik={formik}
												townName="workshop_town"
												addressName="workshop_address"
												zipcodeName="workshop_zipcode"
												cityName="workshop_city"
												streetName="workshop_street"
												stateName="workshop_state"
												addressLabel="Workshop Address"
												townsList={townDropdownData}
												townLabel="Type of Workshop"
												isEditing={isEditing}
											/>
										)}

										<li className={`form-control d-col d-col-2`}>
											<Dropdown
												id="factory_checkbox"
												name="factory_checkbox"
												label="Do you have a factory/mechanized unit?"
												title={formik.values.factory_checkbox ?? ''}
												onBlur={formik.handleBlur}
												options={YesNoVal}
												setFieldValue={formik.setFieldValue}
												error={
													formik.touched.factory_checkbox && formik.errors.factory_checkbox
														? formik.errors.factory_checkbox
														: null
												}
												disabled={isEditing}
												required
											/>
										</li>

										{formik.values.factory_checkbox === 'No' && (
											<li className={`form-control d-col d-col-2`}>
												<Dropdown
													id="work_do_you_generally_undertake"
													name="work_do_you_generally_undertake"
													label="Do you get job work?"
													title={formik.values.work_do_you_generally_undertake ?? ''}
													onBlur={formik.handleBlur}
													options={YesNoVal}
													setFieldValue={formik.setFieldValue}
													error={
														formik.touched.work_do_you_generally_undertake &&
														formik.errors.work_do_you_generally_undertake
															? formik.errors.work_do_you_generally_undertake
															: null
													}
													disabled={isEditing}
													required
												/>
											</li>
										)}

										{(formik.values.factory_checkbox === 'Yes' ||
											formik.values.work_do_you_generally_undertake === 'Yes') && (
											<AddressFields
												mainFieldName="factory"
												formik={formik}
												addressName="factory_address"
												zipcodeName="factory_zipcode"
												cityName="factory_city"
												stateName="factory_state"
												addressLabel="Factory Address"
												isEditing={isEditing}
											/>
										)}

										<li className={`form-control d-col d-col-2`}>
											<Dropdown
												id="interior_work"
												name="interior_work"
												label="Do you take interior work?"
												onBlur={formik.handleBlur}
												title={formik.values.interior_work ?? ''}
												options={YesNoVal}
												setFieldValue={formik.setFieldValue}
												error={
													formik.touched.interior_work && formik.errors.interior_work
														? formik.errors.interior_work
														: null
												}
												disabled={isEditing}
												required
											/>
										</li>

										{formik.values.interior_work === 'No' &&
											formik.values.architect_details.map((architect, index) => (
												<AccordianDetailList
													key={architect.id}
													title="Architect and Interior Designer Details"
													accordianName="architect_details"
													accordianListIndex={index}
													formik={formik}
													data={architect}
													isEditing={isEditing}
													isAddition
												/>
											))}
									</>
								) : (
									<>
										<li className="d-col">
											<Checkbox
												id="workshop_checkbox"
												name="workshop_checkbox"
												type="checkbox"
												checked={formik.values.workshop_checkbox}
												onChange={(e) => {
													formik.handleChange(e);
													handleCheckboxChange(e);
												}}
												label="Do you have a workshop?"
												disabled={isEditing}
											/>
										</li>

										{formik.values.workshop_checkbox && (
											<AddressFields
												mainFieldName="workshop"
												formik={formik}
												townName="workshop_town"
												addressName="workshop_address"
												zipcodeName="workshop_zipcode"
												cityName="workshop_city"
												streetName="workshop_street"
												stateName="workshop_state"
												addressLabel="Workshop Address"
												townsList={townDropdownData}
												townLabel="Type of Workshop"
												isEditing={isEditing}
											/>
										)}

										<li className="d-col">
											<Checkbox
												id="factory_checkbox"
												name="factory_checkbox"
												type="checkbox"
												checked={formik.values.factory_checkbox}
												onChange={(e) => {
													formik.handleChange(e);
													handleCheckboxChange(e);
												}}
												label="Do you have a factory/mechanized unit?"
												disabled={isEditing}
											/>
										</li>

										{formik.values.factory_checkbox && (
											<AddressFields
												mainFieldName="factory"
												formik={formik}
												addressName="factory_address"
												zipcodeName="factory_zipcode"
												cityName="factory_city"
												stateName="factory_state"
												addressLabel="Factory Address"
												isEditing={isEditing}
											/>
										)}

										{formik.values.architect_details.map((architect, index) => (
											<AccordianDetailList
												key={architect.id}
												title="Architect and Interior Designer Details"
												accordianName="architect_details"
												accordianListIndex={index}
												formik={formik}
												data={architect}
												isEditing={isEditing}
												isAddition
											/>
										))}
									</>
								)}

								{isFormEdit &&
									getMoreDetailFormData &&
									(getMoreDetailFormData?.membershipno_c || getMoreDetailFormData?.membershipno) && (
										<SubContractorList
											membershipno_c={getMoreDetailFormData.membershipno_c || getMoreDetailFormData.membershipno}
											isFormEdit={isFormEdit}
											isEditing={isEditing}
											monthlyGluePotential={formik.values.average_glue_potential}
											setFormikValue={() => setFormikValuesToStore(formik.values)}
										/>
									)}
							</ul>
							{checkNextButtonAccessforSS && isRBDMNextBtn && !isImpersonationLoginUser && (
								<SubmitButton
									label="Submit"
									isEditing={isEditing}
									isPending={isMoreDetailFormPending}
									isDisabled={
										!formik.isValid ||
										!formik.dirty ||
										isMoreDetailFormPending ||
										(moreDetailsRevalidate && !areAllFieldsValid)
									}
									setIsEditing={setIsEditing}
									handleSubmitClick={handleSubmitClick}
								/>
							)}
						</form>
					</Wrapper>
				</div>
			</div>
			<Modal
				modalOpen={isOpen}
				modalId={'closeLeads'}
				onModalClose={() => {
					setModal(false);
				}}
				className="dialog-sm close-site-popup dialog-conform"
			>
				<SixMonthsConfirmationModal
					message={'Are you sure you have verified all the highlighted information?'}
					modalClose={() => setModal(false)}
					handleConfirm={() => formik.handleSubmit()}
				/>
			</Modal>

			<Modal
				modalId="confirmationModal"
				className=" dialog-sm opt-confirm pt-25"
				modalOpen={isMoreDetailsUpdateOpen}
				onModalClose={() => setMoreDetailsUpdateOpen(false)}
			>
				<ConfirmationModal
					onClose={() => setMoreDetailsUpdateOpen(false)}
					onSubmit={() => setMoreDetailsUpdateOpen(false)}
					message=""
					question="Kindly review and update the more details fields."
					hideCancelButton
					buttonLabel="Ok"
					className="login-alert"
				/>
			</Modal>
		</>
	);
};
